/**
 * Dispute Message Templates
 * Collection of pre-written message templates for admins to use when responding to disputes
 */

export const disputeMessageTemplates = [
    // Original 18 templates
    {
        id: 'request_info',
        title: 'Request Information',
        message: 'Hello, I need some additional information to help resolve this dispute. Could you please provide more details about the issue?'
    },
    {
        id: 'reviewing',
        title: 'Under Review',
        message: 'Thank you for your message. We are currently reviewing this dispute and will get back to you shortly with an update.'
    },
    {
        id: 'evidence_needed',
        title: 'Evidence Needed',
        message: 'To proceed with this dispute, we need additional evidence. Please provide photos, receipts, or any other relevant documentation that supports your case.'
    },
    {
        id: 'working_on_resolution',
        title: 'Working on Resolution',
        message: 'We are actively working on resolving this dispute. We appreciate your patience and will update you as soon as we have a resolution.'
    },
    {
        id: 'clarification',
        title: 'Need Clarification',
        message: 'Could you please clarify the following points to help us better understand the situation?'
    },
    {
        id: 'update_soon',
        title: 'Update Coming Soon',
        message: 'We are processing your dispute and will provide an update within the next 24-48 hours. Thank you for your patience.'
    },
    {
        id: 'contact_other_party',
        title: 'Contact Other Party',
        message: 'We have reached out to the other party involved in this dispute. We will update you once we receive their response.'
    },
    {
        id: 'investigating',
        title: 'Investigating',
        message: 'We are currently investigating this matter. This may take a few days. We will keep you informed of any developments.'
    },
    {
        id: 'acknowledge_receipt',
        title: 'Acknowledge Receipt',
        message: 'Thank you for bringing this matter to our attention. We have received your dispute and will begin our review process shortly.'
    },
    {
        id: 'partial_resolution',
        title: 'Partial Resolution',
        message: 'After reviewing the evidence from both parties, we have reached a partial resolution. We will work with both parties to finalize the details.'
    },
    {
        id: 'refund_processing',
        title: 'Refund Processing',
        message: 'We have approved your refund request. The refund is being processed and should reflect in your account within 5-7 business days.'
    },
    {
        id: 'replacement_approved',
        title: 'Replacement Approved',
        message: 'We have approved a replacement for your item. The seller will be shipping a replacement item to you. You should receive tracking information shortly.'
    },
    {
        id: 'case_closed',
        title: 'Case Closed',
        message: 'This dispute has been resolved and the case is now closed. If you have any further concerns, please feel free to reach out to our support team.'
    },
    {
        id: 'waiting_response',
        title: 'Waiting for Response',
        message: 'We are currently waiting for a response from the other party. Once we receive their response, we will proceed with the review and provide you with an update.'
    },
    {
        id: 'documentation_review',
        title: 'Reviewing Documentation',
        message: 'We are currently reviewing all the documentation and evidence provided by both parties. This process may take 2-3 business days.'
    },
    {
        id: 'final_decision',
        title: 'Final Decision',
        message: 'After careful review of all evidence and communications, we have reached a final decision on this dispute. Please see the resolution details below.'
    },
    {
        id: 'escalation',
        title: 'Escalation Notice',
        message: 'This dispute has been escalated to our senior review team for further investigation. We will provide you with an update as soon as possible.'
    },
    {
        id: 'payment_issue_resolution',
        title: 'Payment Issue Resolution',
        message: 'We have reviewed the payment issue and are working with the payment processor to resolve this matter. You will be notified once the issue is resolved.'
    },
    {
        id: 'item_verification',
        title: 'Item Verification',
        message: 'We need to verify the condition of the item. Please provide clear photos of the item from multiple angles, including any damage or discrepancies you mentioned.'
    },
    {
        id: 'timeline_update',
        title: 'Timeline Update',
        message: 'We wanted to provide you with an update on the timeline. Due to the complexity of this case, the resolution may take an additional 3-5 business days.'
    },
    {
        id: 'mutual_agreement',
        title: 'Seeking Mutual Agreement',
        message: 'We are working to facilitate a mutual agreement between both parties. We will keep you informed of any progress and will help mediate if needed.'
    },
    {
        id: 'policy_reference',
        title: 'Policy Reference',
        message: 'Based on our terms of service and dispute resolution policy, we are proceeding with the following course of action. If you have any questions, please let us know.'
    },
    {
        id: 'thank_you_patience',
        title: 'Thank You for Patience',
        message: 'We appreciate your patience while we work through this dispute. Your cooperation is valuable in helping us reach a fair resolution for all parties involved.'
    },
    {
        id: 'follow_up',
        title: 'Follow Up',
        message: 'This is a follow-up message to check if you have any additional information or questions regarding this dispute. Please let us know if you need any clarification.'
    },
    {
        id: 'resolution_proposal',
        title: 'Resolution Proposal',
        message: 'We would like to propose the following resolution. Please review and let us know if you agree, or if you would like to discuss any modifications.'
    },
    
    // 50 New Templates
    {
        id: 'refund_approved',
        title: 'Refund Approved',
        message: 'Your refund request has been approved. The amount will be credited back to your original payment method within 7-10 business days. You will receive a confirmation email once the refund is processed.'
    },
    {
        id: 'refund_denied',
        title: 'Refund Request Denied',
        message: 'After reviewing your case, we are unable to approve a refund at this time. The decision is based on our terms of service and the evidence provided. If you have additional information, please share it with us.'
    },
    {
        id: 'seller_contacted',
        title: 'Seller Contacted',
        message: 'We have successfully contacted the seller regarding this dispute. They have been asked to respond within 48 hours. We will update you as soon as we receive their response.'
    },
    {
        id: 'buyer_contacted',
        title: 'Buyer Contacted',
        message: 'We have reached out to the buyer to gather more information about this dispute. Once we receive their response, we will proceed with the review process.'
    },
    {
        id: 'photos_required',
        title: 'Photos Required',
        message: 'To proceed with your dispute, we need clear, high-quality photos of the item in question. Please upload photos showing any damage, defects, or discrepancies you mentioned. Photos should be taken from multiple angles in good lighting.'
    },
    {
        id: 'receipt_required',
        title: 'Receipt Required',
        message: 'Please provide a copy of your purchase receipt or order confirmation. This documentation is essential for us to verify your claim and process your dispute accordingly.'
    },
    {
        id: 'tracking_info_needed',
        title: 'Tracking Information Needed',
        message: 'We need the tracking information for this shipment to verify delivery status. Please provide the tracking number and carrier information if available.'
    },
    {
        id: 'delivery_confirmed',
        title: 'Delivery Confirmed',
        message: 'We have confirmed that the item was delivered according to the tracking information. If you have not received the item, please check with your neighbors or local delivery office, and let us know if you still cannot locate it.'
    },
    {
        id: 'item_damaged',
        title: 'Item Damaged - Replacement Offered',
        message: 'We understand the item arrived damaged. We have arranged for a replacement item to be shipped to you at no additional cost. You should receive tracking information within 24 hours.'
    },
    {
        id: 'wrong_item_received',
        title: 'Wrong Item Received',
        message: 'We apologize for the mix-up. We have arranged for the correct item to be shipped to you. Please keep the incorrect item until you receive further instructions on how to return it.'
    },
    {
        id: 'item_not_as_described',
        title: 'Item Not As Described',
        message: 'We have reviewed your claim that the item does not match the description. Please provide detailed photos and a written explanation of the discrepancies. We will investigate this matter thoroughly.'
    },
    {
        id: 'quality_issue',
        title: 'Quality Issue',
        message: 'We take quality concerns seriously. Please provide detailed photos and a description of the quality issues you have encountered. We will review this and work towards a satisfactory resolution.'
    },
    {
        id: 'size_color_mismatch',
        title: 'Size/Color Mismatch',
        message: 'We understand you received an item in the wrong size or color. We have arranged for the correct item to be sent. Please let us know if you would prefer a refund instead.'
    },
    {
        id: 'missing_parts',
        title: 'Missing Parts or Accessories',
        message: 'We apologize that some parts or accessories are missing from your order. We are arranging to ship the missing items to you. You should receive them within 5-7 business days.'
    },
    {
        id: 'late_delivery',
        title: 'Late Delivery',
        message: 'We apologize for the delay in delivery. We are investigating the cause of the delay with the shipping carrier. We will provide you with an update and work to ensure timely delivery in the future.'
    },
    {
        id: 'undelivered_item',
        title: 'Item Not Delivered',
        message: 'We are investigating why the item was not delivered. Please confirm your delivery address and check with your local delivery office. We will work with the carrier to resolve this issue.'
    },
    {
        id: 'payment_not_received',
        title: 'Payment Not Received',
        message: 'We are investigating the payment issue. Please check your payment method and bank account. If the payment was processed, it may take a few days to reflect. We will update you once we have more information.'
    },
    {
        id: 'overcharged',
        title: 'Overcharged',
        message: 'We are reviewing the charges on your account. If we confirm an overcharge, we will issue a refund for the difference immediately. Please allow 2-3 business days for the refund to process.'
    },
    {
        id: 'duplicate_charge',
        title: 'Duplicate Charge',
        message: 'We have identified a duplicate charge on your account. We will process a refund for the duplicate amount within 3-5 business days. You will receive a confirmation email once the refund is issued.'
    },
    {
        id: 'refund_delayed',
        title: 'Refund Delayed',
        message: 'We apologize for the delay in processing your refund. We are working with the payment processor to expedite this. You should receive your refund within the next 3-5 business days.'
    },
    {
        id: 'cancellation_request',
        title: 'Cancellation Request',
        message: 'We have received your cancellation request. The order has been cancelled and a refund will be processed if payment was already made. You will receive a confirmation email shortly.'
    },
    {
        id: 'return_authorized',
        title: 'Return Authorized',
        message: 'Your return has been authorized. Please use the return label provided to ship the item back. Once we receive and inspect the item, we will process your refund within 5-7 business days.'
    },
    {
        id: 'return_instructions',
        title: 'Return Instructions',
        message: 'Please return the item in its original packaging with all accessories included. Use the provided return label and ship to the address specified. We will process your refund once the item is received.'
    },
    {
        id: 'return_received',
        title: 'Return Received',
        message: 'We have received your returned item and are currently inspecting it. Once the inspection is complete, we will process your refund. This typically takes 2-3 business days.'
    },
    {
        id: 'return_rejected',
        title: 'Return Rejected',
        message: 'Unfortunately, we cannot accept this return as the item does not meet our return policy requirements. The item will be shipped back to you. If you have questions, please contact us.'
    },
    {
        id: 'warranty_claim',
        title: 'Warranty Claim',
        message: 'We have received your warranty claim. Please provide proof of purchase and a detailed description of the issue. We will review your claim and determine the appropriate course of action under the warranty terms.'
    },
    {
        id: 'warranty_approved',
        title: 'Warranty Approved',
        message: 'Your warranty claim has been approved. We will either repair, replace, or refund the item according to the warranty terms. You will receive further instructions via email.'
    },
    {
        id: 'warranty_denied',
        title: 'Warranty Denied',
        message: 'After reviewing your warranty claim, we are unable to approve it at this time as it does not meet the warranty terms and conditions. If you believe this is an error, please provide additional documentation.'
    },
    {
        id: 'account_verification',
        title: 'Account Verification Needed',
        message: 'To proceed with your dispute, we need to verify your account. Please confirm your email address and provide any additional verification documents if requested.'
    },
    {
        id: 'identity_verification',
        title: 'Identity Verification',
        message: 'For security purposes, we need to verify your identity. Please provide a government-issued ID and a recent utility bill or bank statement. This information will be kept confidential and secure.'
    },
    {
        id: 'additional_documents',
        title: 'Additional Documents Required',
        message: 'We need additional documentation to process your dispute. Please provide the requested documents as soon as possible so we can continue with the review process.'
    },
    {
        id: 'evidence_reviewed',
        title: 'Evidence Reviewed',
        message: 'We have reviewed all the evidence you provided. Thank you for your thorough documentation. We are now proceeding with the next steps in the dispute resolution process.'
    },
    {
        id: 'third_party_involved',
        title: 'Third Party Involved',
        message: 'This dispute involves a third party (shipping carrier, payment processor, etc.). We are coordinating with them to resolve this matter. This may extend the resolution timeline slightly.'
    },
    {
        id: 'legal_review',
        title: 'Legal Review',
        message: 'This dispute requires legal review due to its complexity. We have escalated it to our legal team. This process may take longer than usual, but we will keep you informed of any updates.'
    },
    {
        id: 'fraud_investigation',
        title: 'Fraud Investigation',
        message: 'We are conducting a fraud investigation related to this dispute. This is a serious matter and requires thorough examination. We will update you as the investigation progresses.'
    },
    {
        id: 'account_suspended',
        title: 'Account Suspended',
        message: 'Due to suspicious activity related to this dispute, we have temporarily suspended the account in question pending investigation. We will provide updates as the investigation continues.'
    },
    {
        id: 'compensation_offered',
        title: 'Compensation Offered',
        message: 'We understand the inconvenience you have experienced. As a gesture of goodwill, we are offering compensation. Please let us know if you accept this offer or if you would like to discuss alternatives.'
    },
    {
        id: 'discount_offered',
        title: 'Discount Offered',
        message: 'We apologize for the inconvenience. We would like to offer you a discount on your next purchase as compensation. A discount code will be sent to your email shortly.'
    },
    {
        id: 'store_credit',
        title: 'Store Credit Offered',
        message: 'We are offering you store credit as a resolution to this dispute. The credit will be added to your account and can be used for future purchases. You will receive a confirmation email with the credit amount.'
    },
    {
        id: 'apology',
        title: 'Apology',
        message: 'We sincerely apologize for the inconvenience and frustration this situation has caused. We take full responsibility and are committed to resolving this matter to your satisfaction.'
    },
    {
        id: 'thank_you_cooperation',
        title: 'Thank You for Cooperation',
        message: 'Thank you for your cooperation and patience throughout this dispute process. Your understanding and assistance have been invaluable in helping us reach a resolution.'
    },
    {
        id: 'resolution_accepted',
        title: 'Resolution Accepted',
        message: 'We are pleased that you have accepted the proposed resolution. We will proceed with implementing the agreed-upon solution. You will receive confirmation once everything is finalized.'
    },
    {
        id: 'resolution_rejected',
        title: 'Resolution Rejected',
        message: 'We understand that the proposed resolution does not meet your expectations. We would like to discuss alternative solutions. Please let us know what would be an acceptable resolution for you.'
    },
    {
        id: 'counter_offer',
        title: 'Counter Offer',
        message: 'We have received your counter offer and are reviewing it. We will get back to you within 24-48 hours with our response. Thank you for working with us to find a mutually acceptable solution.'
    },
    {
        id: 'mediation_suggested',
        title: 'Mediation Suggested',
        message: 'Given the complexity of this dispute, we suggest involving a neutral third-party mediator to help facilitate a resolution. Would you be willing to participate in mediation?'
    },
    {
        id: 'arbitration_notice',
        title: 'Arbitration Notice',
        message: 'As per our terms of service, this dispute will proceed to binding arbitration if we cannot reach a mutual agreement. We will provide you with details about the arbitration process.'
    },
    {
        id: 'case_reopened',
        title: 'Case Reopened',
        message: 'We have reopened your case based on new information you provided. We will conduct a fresh review and will update you with our findings and resolution.'
    },
    {
        id: 'case_archived',
        title: 'Case Archived',
        message: 'This dispute case has been archived as it has been resolved and closed. If you have any new concerns or questions, please open a new dispute or contact our support team.'
    },
    {
        id: 'feedback_request',
        title: 'Feedback Request',
        message: 'We would appreciate your feedback on how we handled this dispute. Your input helps us improve our service. Please take a moment to share your experience with us.'
    },
    {
        id: 'preventive_measures',
        title: 'Preventive Measures',
        message: 'We have implemented measures to prevent similar issues from occurring in the future. We appreciate you bringing this matter to our attention and helping us improve our service.'
    }
];

